import {classNames, observer, React, useMemo} from '@api'
import {Icon} from '@blueprintjs/core'
import {Chip} from '@material-ui/core'
import {ToolbarChip} from '@modules'
import {GroupByToolbarChipsStore, useGroupByToolbarChipsStore} from './'
import {GroupByGrouping, StoresInstance} from '@stores'
import {action, computed, runInAction} from 'mobx'
import {DragEventHandler, useContext} from 'react'
import {VipLocalStore} from '@modules'
import * as css from './GroupByToolbarChip.css'

/// region Store

export const GroupByMenuChipContext = React.createContext<GroupByMenuChipStore>(undefined)
export const useGroupByMenuChipStore = () => useContext(GroupByMenuChipContext)

export class GroupByMenuChipStore extends VipLocalStore {
  constructor(stores: StoresInstance,public group: GroupByGrouping, public store: GroupByToolbarChipsStore) {
    super(stores)
  }

  @action onMouseEnterMainGroup = (e) => {
    const {groupBy, groupBy: {menuStore: {subtitleButtonActive}}, store, store: {selectedSubtitle}, group, group: {indexInHierarchy}} = this
    if (selectedSubtitle) {
      store.selectedSubtitle = null
      store.selectedIndex = indexInHierarchy
      groupBy.menuStore.subtitleButtonActive = false
    } else {
      store.selectedSubtitle = null
      if (subtitleButtonActive) {
        groupBy.menuStore.subtitleButtonActive = false
        store.selectedIndex = indexInHierarchy
      } else {
        if (store.selectedGroup === group) {
          store.selectedIndex = null
        } else {
          store.selectedIndex = indexInHierarchy
        }
      }
    }
  }

  @action onClickMainGroup = (e) => {
    const {groupBy, groupBy: {menuStore: {subtitleButtonActive}}, store, store: {selectedSubtitle}, group, group: {indexInHierarchy}} = this
    if (selectedSubtitle) {
      store.selectedSubtitle = null
      store.selectedIndex = indexInHierarchy
      groupBy.menuStore.subtitleButtonActive = false
    } else {
      store.selectedSubtitle = null
      if (subtitleButtonActive) {
        groupBy.menuStore.subtitleButtonActive = false
        store.selectedIndex = indexInHierarchy
      } else {
        if (store.selectedGroup === group) {
          store.selectedIndex = null
        } else {
          store.selectedIndex = indexInHierarchy
        }
      }
    }
  }

  @action onClickSubtitle = (field: string) => (e) => {
    const {groupBy, groupBy: {menuStore: {subtitleButtonActive}}, store, group, group: {indexInHierarchy}} = this
    if (!subtitleButtonActive) {
      store.selectedIndex = indexInHierarchy
      store.selectedSubtitle = field
      groupBy.menuStore.subtitleButtonActive = true
    } else {
      if (store.selectedSubtitle == field) {
        store.selectedIndex = null
        store.selectedSubtitle = null
      } else {
        groupBy.menuStore.subtitleButtonActive = true
        store.selectedIndex = indexInHierarchy
        store.selectedSubtitle = field
      }
    }
  }

  @action onClick = e => {
    const {store, group} = this
    if (store.selectedGroup === group) {
      store.selectedIndex = null
    } else {
      store.selectedIndex = group.indexInHierarchy
    }
  }

  @computed get leftIcon() {
    const {group} = this
    if (!group || group.isCatalogItemsGrouping) return undefined

    const {level} = group
    return level == 0 ? 'header-one' : level == 1 ? 'header-two' : 'header'
  }

  get dragMouseEvents() {
    const {onDragStart, onDragOver, onDragLeave, onDragEnter, onDragEnd, onDrop} = this
    return {onDragStart, onDragOver, onDragLeave, onDragEnter, onDragEnd, onDrop}
  }

  @computed get showDropPlaceholder() {
    const {group, store: {dragOverItem, dragItem}} = this
    return group == dragOverItem && group != dragItem
  }

  @action onDragStart: DragEventHandler = (e: React.DragEvent) => {
    e.stopPropagation()

    e.dataTransfer.effectAllowed = 'move'

    this.store.dragItem = this.group
    this.dragDrop.setDragItem(e, {id: this.group.groupName, dragType: 'group'}, this.catalog)
  }

  @action onDrop: DragEventHandler = e => {
    e.stopPropagation()
    const {groupBy, store, store: {dragOverItem, dragItem}} = this
    console.log(`onDrop - `, {dragItem, dragOverItem})
    if (dragItem !== dragOverItem) {
      runInAction(() => {
        groupBy.swapItemsInHierarchy(dragItem, dragOverItem)
        store.clearDragState()
      })
    }
  }

  @action onDragOver = e => {
    e.preventDefault()
  }

  @action onDragEnd = e => {
    this.store.clearDragState()
    this.dragDrop.reset()
  }

  get groups() {
    return this.groupBy.hierarchyGroups
  }

  @computed get dropdownStore() {
    return this.groupBy.dropdownMenuStores.get(this.group?.level ?? 0)
  }

  @action onDragEnter: DragEventHandler = e => {
    e.stopPropagation()
    e.preventDefault()

    if (this.store.dragItem) {
      this.store.dragOverItem = this.group
    }
  }

  @action onDragLeave: DragEventHandler = e => {
    e.preventDefault()
    e.stopPropagation()
    const {store, store: {dragItem, dragOverItem}} = this
    if (dragItem && dragOverItem) {
      const {relatedTarget, currentTarget} = e

      let hasLeftElement = false
      // Chrome and Firefox
      if (relatedTarget) {
        hasLeftElement = !currentTarget.contains(relatedTarget as Node)
      } else {
        // Safari

        // NOTE: relatedTarget is not supported in Safari yet (https://bugs.webkit.org/show_bug.cgi?id=66547)
        // and therefore, had to do below hack to verify if mouse pointer has moved out of currentTarget
        // Expensive!
        let rect = currentTarget.getBoundingClientRect()
        hasLeftElement = e.clientX >= (rect.left + rect.width) || e.clientX <= rect.left || e.clientY >= (rect.top + rect.height) || e.clientY <= rect.top
      }
      if (dragItem && hasLeftElement) {
        store.dragOverItem = null
      }
    }
  }
}

/// endregion

export interface CatalogHierarchyFilterChipProps {
  group: GroupByGrouping,
  index: number
  className?: string
}

/**
 * A draggable layer around a group by dropdown button
 */
export const GroupByToolbarChip = observer<CatalogHierarchyFilterChipProps>(
  function GroupByToolbarChip({group, className, index}) {
    const parentStore = useGroupByToolbarChipsStore(),
      store = useMemo(() => new GroupByMenuChipStore(parentStore.stores, group, parentStore), [group, parentStore]),
      {dragMouseEvents, onClickSubtitle, catalog} = store,
      {selectedSubtitle} = parentStore

    if (!group) return null

    const subtitles = (group.vlpType ? group.mySubtitleFieldsNonHierarchy : group.mySubtitleFields).filter(({field}) => !group.groupDefinition?.subtitle?.find(f => f.field == field))

    return (
      <GroupByMenuChipContext.Provider value={store}>
        <div className={css.root}
             data-type='GroupByToolbarChip'
             data-open={parentStore?.isOpen && parentStore.selectedGroup === group}
             data-subtitles={subtitles.length}
             data-selected-subtitle={selectedSubtitle ?? undefined}
             {..._.omit(dragMouseEvents, 'onDragStart')}
        >
          <GroupByToolbarChipComponent className={className}
                                       group={group}
                                       icon={<Icon icon={store.leftIcon} className={css.icon}/>}
                                       onDelete={!catalog.isProductView && !group.isCatalogItemsGrouping ? group.removeFromHierarchy : undefined}/>

          {!_.isEmpty(subtitles) && <div className={css.subtitleChips}>
            {subtitles.map((f, i) => (
              <Chip
                key={f.field}
                className={classNames(css.chip, css.subtitleChip)}
                data-type='SubtitleChip'
                data-field={f.field}
                data-selected={f.field === selectedSubtitle}
                onClick={onClickSubtitle(f.field)}
                label={
                  <span className={css.labelText}>
                            <div className={css.column}>
                              <div className={css.groupName}>
                                <Icon icon={<div className={css.subtitleIcon}>S<sub>{i + 1}</sub></div>}
                                      className={css.icon}/>
                                <span className={css.groupTitle}>{catalog.labelForFieldName_currentCatalog(f.field)}</span>
                              </div>
                            </div>
                          </span>}
                onDelete={() => group.toggleSubtitleField(f.field)}
              />
            ))}
		  </div>}
        </div>
      </GroupByMenuChipContext.Provider>
    )
  })

export const GroupByToolbarChipComponent = observer<{ className?: string, group: GroupByGrouping, icon?: React.ReactNode, onDelete?: () => void, noCaret?: boolean }>(function GroupByToolbarChip_Main({className, icon, group, onDelete, noCaret}) {
  const {level} = group
  const {dragMouseEvents, showDropPlaceholder, groupBy, onClickMainGroup, onMouseEnterMainGroup, store: parentStore} = useGroupByMenuChipStore() ?? {}

  return (<ToolbarChip
    className={classNames(css.chip, className)}
    data-type='GroupChip'
    data-field={group.fieldName}
    data-drop-target={showDropPlaceholder}
    data-open={parentStore?.isOpen && parentStore?.selectedGroup === group}
    data-level={level}
    onClick={onClickMainGroup}
    // onMouseEnter={onMouseEnterMainGroup}
    draggable={groupBy?.hierarchy?.length > 1 && !group.isCatalogItemsGrouping}
    onDragStart={dragMouseEvents?.onDragStart}
    chipLabel={
      (<span className={css.labelText}>
                        <div className={css.column}>
                          <div className={css.groupName}>
                            {icon ?? <div className={css.groupMainIcon}>H<sub>{level + 1}</sub></div>}
                            <span className={css.groupTitle}>{group.title}</span>
                          </div>
                        </div>
        {!noCaret && <Icon icon='caret-down'/>}
                      </span>
      )
    }
    onDelete={onDelete}
  />)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Menus/Toolbar/GroupChips/GroupByToolbarChip.tsx